import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CopyIcon, XIcon } from 'lucide-react';
import { useCountryData } from '../hooks/useCountryData';
import { useScenario } from '../contexts/ScenarioContext';
import { CountryRow } from '../types/census';
import { agentsPerCapitaFor, countryMachines } from '../utils/estimates';
import { formatCompact, formatPct } from '../utils/format';
import { buildersIn, modelsFrom, platformsFrom } from '../utils/ecosystem';
import { AspopResult, getAspop } from '../utils/apnic';
import { speedIndex } from '../data/speedIndex';
import { BRAND } from '../data/brand';
import { PageHeader } from '../components/PageHeader';
import { PageLoader } from '../components/PageLoader';
import { SourceRef } from '../components/SourceRef';
import { DataBadge } from '../components/DataBadge';
const MAX = 4;
function parseCodes(search: string): string[] {
  const raw = decodeURIComponent(search.replace(/^\?/, '')).split(/[&,=]/).map(s => s.trim()).filter(s => s && s.toLowerCase() !== 'c');
  return Array.from(new Set(raw.map(s => s.toUpperCase())));
}
interface Metric {
  label: string;
  sourceId?: string;
  estimate?: boolean;
  value: (r: CountryRow) => string;
  /** Numeric for highlighting the leader; null to skip. */
  num?: (r: CountryRow) => number | null;
}
export function Compare() {
  const data = useCountryData();
  const [params, setParams] = useSearchParams();
  const {
    scenarioId,
    scenario
  } = useScenario();
  const [aspop, setAspop] = useState<Record<string, AspopResult>>({});
  const [copied, setCopied] = useState(false);
  const selected: CountryRow[] = useMemo(() => {
    const codes = parseCodes(window.location.search || `?${params.toString()}`);
    const out: CountryRow[] = [];
    codes.forEach(c => {
      const r = data.rows.find(x => x.cca3 === c || x.cca2 === c);
      if (r && !out.includes(r) && out.length < MAX) out.push(r);
    });
    return out;
  }, [params, data.rows]);
  useEffect(() => {
    let alive = true;
    selected.forEach(r => {
      if (aspop[r.cca3]) return;
      getAspop(r.cca2).then(res => alive && setAspop(p => ({
        ...p,
        [r.cca3]: res
      })));
    });
    return () => {
      alive = false;
    };
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const setCodes = (codes: string[]) => setParams(codes.length ? {
    c: codes.join(',')
  } : {}, {
    replace: true
  });
  const add = (cca3: string) => setCodes([...selected.map(r => r.cca3), cca3].slice(0, MAX));
  const remove = (cca3: string) => setCodes(selected.filter(r => r.cca3 !== cca3).map(r => r.cca3));
  const shareUrl = `${BRAND.url}/compare?c=${selected.map(r => r.cca3.toLowerCase()).join(',')}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };
  if (data.loading) return <PageLoader label="Loading countries" data-id="element-993" data-mp="8hz6vpiy9m2r" />;
  const pc = (r: CountryRow) => agentsPerCapitaFor(r, scenarioId);
  const metrics: Array<{
    group: string;
    rows: Metric[];
  }> = [{
    group: 'People & connectivity',
    rows: [{
      label: 'Population',
      sourceId: 'restcountries',
      value: r => formatCompact(r.population),
      num: r => r.population
    }, {
      label: 'Internet users',
      sourceId: 'wbInternet',
      value: r => r.internetPct ? `${formatPct(r.internetPct.value)} · ${formatCompact(r.internetUsers ?? 0)}` : '—',
      num: r => r.internetPct?.value ?? null
    }, {
      label: 'Mobile subscriptions / 100',
      sourceId: 'wbMobile',
      value: r => r.mobilePer100 ? r.mobilePer100.value.toFixed(1) : '—',
      num: r => r.mobilePer100?.value ?? null
    }, {
      label: 'Median age',
      sourceId: 'unwpp',
      value: r => r.medianAge != null ? `${r.medianAge.toFixed(1)} yrs` : '—'
    }, {
      label: 'Working-age share',
      sourceId: 'wbWorking',
      value: r => r.workingAgePct ? formatPct(r.workingAgePct.value) : '—'
    }, {
      label: 'Speed fixed / mobile',
      sourceId: 'ookla',
      value: r => speedIndex[r.cca3] ? `${speedIndex[r.cca3].fixed} / ${speedIndex[r.cca3].mobile} Mbps` : '—',
      num: r => speedIndex[r.cca3]?.fixed ?? null
    }]
  }, {
    group: `Machines · ${scenario.label}`,
    rows: [{
      label: 'Assistant endpoints',
      sourceId: 'censusModel',
      estimate: true,
      value: r => r.endpoints != null ? formatCompact(r.endpoints) : '—',
      num: r => r.endpoints
    }, {
      label: 'Machines (scenario)',
      sourceId: 'censusModel',
      estimate: true,
      value: r => {
        const m = countryMachines(r, scenarioId);
        return m != null ? formatCompact(m) : '—';
      },
      num: r => countryMachines(r, scenarioId)
    }, {
      label: 'Agents per capita',
      sourceId: 'censusModel',
      estimate: true,
      value: r => {
        const v = pc(r);
        return v != null ? v < 0.1 ? v.toFixed(4) : v.toFixed(2) : '—';
      },
      num: pc
    }]
  }, {
    group: 'AI ecosystem',
    rows: [{
      label: 'Builders',
      sourceId: 'censusCurated',
      value: r => String(buildersIn(r.cca3).length),
      num: r => buildersIn(r.cca3).length
    }, {
      label: 'Models released',
      sourceId: 'censusCurated',
      value: r => {
        const m = modelsFrom(r.cca3);
        return `${m.length} (${m.filter(x => x.license !== 'proprietary').length} open)`;
      },
      num: r => modelsFrom(r.cca3).length
    }, {
      label: 'Platforms',
      sourceId: 'censusCurated',
      value: r => String(platformsFrom(r.cca3).length),
      num: r => platformsFrom(r.cca3).length
    }]
  }, {
    group: 'Networks',
    rows: [{
      label: 'Largest network',
      sourceId: 'apnicAspop',
      value: r => {
        const top = aspop[r.cca3]?.rows[0];
        return top ? `${top.name} (${top.asn})` : aspop[r.cca3] ? '—' : '…';
      }
    }, {
      label: 'Its users',
      sourceId: 'apnicAspop',
      estimate: true,
      value: r => {
        const top = aspop[r.cca3]?.rows[0];
        return top ? formatCompact(top.users) : '—';
      },
      num: r => aspop[r.cca3]?.rows[0]?.users ?? null
    }]
  }];
  const leader = (m: Metric): string | null => {
    if (!m.num || selected.length < 2) return null;
    let best: {
      c: string;
      v: number;
    } | null = null;
    selected.forEach(r => {
      const v = m.num?.(r);
      if (v != null && (!best || v > best.v)) best = {
        c: r.cca3,
        v
      };
    });
    return best ? (best as {
      c: string;
      v: number;
    }).c : null;
  };
  const options = [...data.rows].sort((a, b) => a.name.localeCompare(b.name)).filter(r => !selected.includes(r));
  return <>
      <PageHeader title="Compare" lede="Two to four countries side by side: people, machines, builders, models and networks. The URL updates as you pick, so you can share the view." data-id="element-994" data-mp="39hp4z3tohqzg">
        <DataBadge mode={data.modes.countries} source="REST Countries + World Bank" asOf={data.asOf.countries} data-id="element-995" data-mp="2zr2ufo0tjqnm" />
      </PageHeader>

      <section className="mx-auto max-w-[1400px] px-5 md:px-8" data-id="element-996" data-mp="2lt5xgqigdi8f">
        <div className="flex flex-wrap items-center gap-3 py-6" data-id="element-997" data-mp="2prvi945tm223">
          {selected.map(r => <span key={r.cca3} className="inline-flex items-center gap-2 rounded-full border border-line py-1 pl-2 pr-1 text-[13.5px] text-ink" data-id="element-998" data-mp="274mnk4ak2vc7">
              <img src={r.flag} alt="" className="h-3.5 w-5 rounded-[2px] object-cover ring-1 ring-line" data-id="element-999" data-mp="2g4rgj9f05r6u" />
              {r.name}
              <button type="button" onClick={() => remove(r.cca3)} aria-label={`Remove ${r.name}`} className="flex h-6 w-6 items-center justify-center rounded-full text-muted hover:bg-raised hover:text-ink" data-id="element-1000" data-mp="2b4n1lntqt4iq">
                <XIcon className="h-3.5 w-3.5" data-id="element-1001" data-mp="2l32tvu4iajtt" />
              </button>
            </span>)}
          {selected.length < MAX && <label className="flex items-center gap-2 text-[13px] text-muted" data-id="element-1002" data-mp="2yx3ku1p7v2jm">
              <span className="sr-only" data-id="element-1003" data-mp="2uugyuhf88buc">Add a country</span>
              <select value="" onChange={e => e.target.value && add(e.target.value)} className="rounded-md border border-line bg-surface px-2.5 py-1.5 text-[13px] text-ink focus:border-machine focus:outline-none" data-id="element-1004" data-mp="38snfkks1nv6s">
                <option value="" data-id="element-1005" data-mp="f0n75wlvc5j7">+ Add country</option>
                {options.map(r => <option key={r.cca3} value={r.cca3} data-id="element-1006" data-mp="j1yxy5ghawsz">
                    {r.name}
                  </option>)}
              </select>
            </label>}
          {selected.length > 0 && <button type="button" onClick={copy} className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-line px-3 py-1.5 text-[13px] text-ink hover:border-machine" data-id="element-1007" data-mp="t0objm1cdv3f">
              <CopyIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1008" data-mp="daku5o8lvrr" /> {copied ? 'Link copied' : 'Copy share link'}
            </button>}
        </div>

        {selected.length < 2 ? <div className="rounded-lg border border-dashed border-line px-6 py-14 text-center" data-id="element-1009" data-mp="a33vn4gk26uy">
            <p className="text-ink" data-id="element-1010" data-mp="38rap8pp6rutf">Pick at least two countries to compare.</p>
            <p className="mt-2 text-[13px] text-muted" data-id="element-1011" data-mp="3dqvu1gr1exe2">
              Or try{' '}
              <Link to="/compare?c=ind,usa,chn" className="text-machine underline underline-offset-2" data-id="element-1012" data-mp="367miphyz0omd">
                India · United States · China
              </Link>
              .
            </p>
          </div> : <div className="overflow-x-auto rounded-lg border border-line" data-id="element-1013" data-mp="2w8oy2q4o1f8n">
            <table className="w-full min-w-[720px] border-collapse text-[13.5px]" data-id="element-1014" data-mp="2rb56ypmvd7za">
              <thead className="bg-surface" data-id="element-1015" data-mp="2n91upysdlzcy">
                <tr data-id="element-1016" data-mp="2n9tr3btss14a">
                  <th scope="col" className="w-56 border-b border-line px-4 py-3 text-left text-[12px] font-normal text-muted" data-id="element-1017" data-mp="2nacyvzpqh94l">
                    Metric
                  </th>
                  {selected.map(r => <th key={r.cca3} scope="col" className="border-b border-line px-4 py-3 text-left font-normal" data-id="element-1018" data-mp="2dgw16kmh79vn">
                      <Link to={`/countries/${r.cca3}`} className="inline-flex items-center gap-2 font-serif text-lg text-ink hover:text-machine" data-id="element-1019" data-mp="23kt0zkb6plbc">
                        <img src={r.flag} alt="" className="h-4 w-6 rounded-[2px] object-cover ring-1 ring-line" data-id="element-1020" data-mp="1zjzfsyp4mkm5" />
                        {r.name}
                      </Link>
                    </th>)}
                </tr>
              </thead>
              {metrics.map(g => <tbody key={g.group} data-id="element-1021" data-mp="3pymkg6rineum">
                  <tr data-id="element-1022" data-mp="3l02w5zpg9qz3">
                    <th colSpan={selected.length + 1} scope="colgroup" className="border-b border-line bg-bg px-4 pb-2 pt-5 text-left text-[12.5px] font-medium text-machine" data-id="element-1023" data-mp="3kzjgkpn9veo8">
                      {g.group}
                    </th>
                  </tr>
                  {g.rows.map(m => {
              const lead = leader(m);
              return <tr key={m.label} className="border-b border-line/60" data-id="element-1024" data-mp="368u31gmc9nh7">
                        <th scope="row" className="px-4 py-2.5 text-left font-normal text-muted" data-id="element-1025" data-mp="328a6ae5oglvl">
                          {m.label}
                          {m.sourceId && <SourceRef id={m.sourceId} estimate={m.estimate ? {
                    low: '−15%',
                    high: '+15%'
                  } : undefined} data-id="element-1026" data-mp="hjvwe4a5odvl" />}
                        </th>
                        {selected.map(r => <td key={r.cca3} className={`px-4 py-2.5 font-mono text-[13px] tabular ${lead === r.cca3 ? 'text-ink' : 'text-ink/75'}`} data-id="element-1027" data-mp="u2hr2dtvxbl4">
                            {m.value(r)}
                            {lead === r.cca3 && <span className="ml-2 font-sans text-[10.5px] text-machine" data-id="element-1028" data-mp="k4tstdx2lly7">highest</span>}
                          </td>)}
                      </tr>;
            })}
                </tbody>)}
            </table>
          </div>}
      </section>
    </>;
}